import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  ChevronRight,
  Download,
  Info,
  TriangleAlert,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { localizedName } from '@/components/localized-name';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { paymentMethodLabelKey } from '@/features/events/payment-methods';
import { VoucherReportTiles } from '@/features/gift-vouchers/voucher-report-tiles';
import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPhone,
} from '@/lib/format';
import { useClientFlags } from '@/config/client-flags';
import { cn } from '@/lib/utils';
import { OverviewRowsSheet } from './overview-rows-sheet';
import { Meter, SectionTitle, StatGroup, StatTile } from '@/components/ui/stat-tile';
import {
  toApiRange,
  useBookingLocations,
  useOverviewDashboard,
  type OverviewSearchModel,
} from './overview.api';
import {
  METRIC,
  SOURCE,
  SOURCE_VAR,
  sourceLabelKey,
  type OverviewMember,
  type OverviewSource,
  type PeopleListKey,
} from './types';

/**
 * The combined business overview — memberships, bookings and gifts.
 *
 * Three tabs rather than one long scroll, and not only because of length: the
 * page answers three questions on two different bases (money on the day it
 * arrived, activity and people on the day of the class). A tab per question
 * lets each state its basis once, at the top, and be read without the others in
 * the way.
 */

const ANY = 'ANY';

/**
 * The location filter has three states and a Select needs one value per option,
 * so the third gets a sentinel. The API models it as its own boolean rather
 * than a magic id, so this string never leaves the page.
 */
const NO_LOCATION = '__none__';

const firstOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
};

const lastOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0)
    .toISOString()
    .slice(0, 10);
};

/** The API sends shares as 0–100 already; keep one rounding rule for all of them. */
const pct = (value: number | null | undefined) =>
  `${(value ?? 0).toFixed(Math.abs(value ?? 0) < 10 ? 1 : 0)}%`;

const pctNumber = (value: number | null | undefined) => value ?? 0;

/** Rows per page inside an opened revenue source. */
const BREAKDOWN_PAGE_SIZE = 8;

/** An info or warning strip above a tab's content. */
function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warning';
  children: React.ReactNode;
}) {
  const Icon = tone === 'warning' ? TriangleAlert : Info;
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-xl border p-3 text-sm',
        tone === 'warning'
          ? 'border-warning/30 bg-warning/10'
          : 'bg-muted border-border'
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 size-4 shrink-0',
          tone === 'warning' ? 'text-warning' : 'text-muted-foreground'
        )}
      />
      <p>{children}</p>
    </div>
  );
}

/** One figure of a table row, label above value, so a card reads as a table would. */
function Cell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-sm tabular-nums">{children}</p>
    </div>
  );
}

/** Stands in for a card list on first load, so "no data" is not said before it is known. */
function ListSkeleton() {
  return (
    <div className="divide-y rounded-xl border">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="flex items-center gap-6 p-3">
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="hidden h-2 w-56 sm:block" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
    </div>
  );
}

/** An empty list that may be about to fill: a refresh still shows its spinner. */
function EmptyList({ busy }: { busy: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="relative rounded-xl">
      <BusyOverlay show={busy} />
      <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
        {t('overview.noData')}
      </p>
    </div>
  );
}

export function OverviewPage() {
  const { t, i18n } = useTranslation();
  const { hasBookingVouchers } = useClientFlags();
  const language = i18n.resolvedLanguage ?? 'en';

  const [start, setStart] = useState(firstOfMonth);
  const [end, setEnd] = useState(lastOfMonth);
  const [locationId, setLocationId] = useState(ANY);
  const [peopleList, setPeopleList] =
    useState<PeopleListKey>('topBenefitUsers');

  const [drill, setDrill] = useState<{
    metric: number;
    title: string;
    source: number | null;
  } | null>(null);

  const { data: locations } = useBookingLocations();

  const noLocation = locationId === NO_LOCATION;

  const model: OverviewSearchModel = useMemo(
    () => ({
      ...toApiRange(start, end),
      locationId: locationId === ANY || noLocation ? null : locationId,
      noLocation,
    }),
    [start, end, locationId, noLocation]
  );

  const dashboard = useOverviewDashboard(model);
  const loading = dashboard.isPending;
  // A refresh keeps the figures on screen and dims them; only a first
  // load, with nothing to dim, gets skeletons.
  const busy = dashboard.isFetching && !dashboard.isPending;
  const data = dashboard.data;

  const money = data?.money;
  const recurring = data?.recurring;
  const bySource = data?.bySource ?? [];
  const byChannel = data?.byChannel ?? [];
  const series = data?.series ?? [];
  const activity = data?.activity;
  const plans = data?.plans ?? [];
  const events = data?.events ?? [];
  const members = data?.members;

  const peak = Math.max(0, ...series.map((day) => day.total ?? 0));

  /**
   * Opening a tile is opening a question about it: which rows made this number.
   */
  const drillInto = (metric: number, title: string, source: number | null = null) =>
    setDrill({ metric, title, source });

  const filterFields: FilterField[] = [
    {
      id: 'start',
      label: t('overview.from'),
      value: start,
      onChange: setStart,
      type: 'date',
      resetValue: '',
    },
    {
      id: 'end',
      label: t('overview.to'),
      value: end,
      onChange: setEnd,
      type: 'date',
      resetValue: '',
    },
    {
      id: 'location',
      label: t('overview.location'),
      value: locationId,
      onChange: setLocationId,
      // "Any" and "No location" are both ways of saying "not one of the ones
      // below"; sorting the second alphabetically would file it under N.
      sort: false,
      options: [
        { value: ANY, label: t('overview.any') },
        { value: NO_LOCATION, label: t('overview.noLocation') },
        ...[...(locations ?? [])]
          .map((l) => ({
            value: l.id,
            label: localizedName(l, language) || (l.name ?? ''),
          }))
          .sort((a, b) =>
            a.label.localeCompare(b.label, language, { sensitivity: 'base' })
          ),
      ],
    },
  ];

  const selectedLocation = (locations ?? []).find((l) => l.id === locationId);

  const summary = (
    <p className="text-muted-foreground text-sm">
      {t('overview.showing')}{' '}
      <b className="text-foreground">
        {formatDate(start)} – {formatDate(end)}
      </b>
      {noLocation
        ? ` ${t('overview.summaryNoLocation')}`
        : selectedLocation
          ? ` ${t('overview.summaryAt')} ${localizedName(selectedLocation, language) || selectedLocation.name}`
          : ` ${t('overview.summaryEveryLocation')}`}
      .
    </p>
  );

  /**
   * "$3,120 last month" rather than "+12%". A percentage alone makes you do
   * arithmetic to find out what it is a percentage of, and at a month-end
   * review the previous figure is what people actually want. The percentage
   * rides along when it can be computed — it cannot from a previous period of
   * zero, and inventing "+100%" there would be a lie.
   */
  function changeLabel(row: OverviewSource) {
    if (!data) return null;
    const range = `${formatDate(data.previousStartDate)} – ${formatDate(data.previousEndDate)}`;
    if (row.change === null || row.change === undefined) {
      return (row.previousCollected ?? 0) > 0
        ? `${formatCurrency(row.previousCollected)} · ${range}`
        : t('overview.nothingPreviousPeriod');
    }
    const up = row.change >= 0;
    return `${up ? '▲' : '▼'} ${pct(Math.abs(row.change))} · ${formatCurrency(row.previousCollected)} ${range}`;
  }

  /**
   * The sub-line under "No payment recorded": what the seats were worth, then
   * the split between a comp somebody signed and a booking nobody can account
   * for. The needs-review half is only mentioned when there is one — a
   * permanent "· 0 needs review" reads as a category to keep checking.
   */
  function noPaymentLabel() {
    if (!money) return null;
    const parts = [
      `${formatCurrency(money.noPaymentValue)} ${t('overview.value').toLowerCase()}`,
    ];
    if ((money.compedBookings ?? 0) > 0) {
      parts.push(
        `${formatNumber(money.compedBookings)} ${t('overview.comped').toLowerCase()}`
      );
    }
    if ((money.needsReviewBookings ?? 0) > 0) {
      parts.push(
        `${formatNumber(money.needsReviewBookings)} ${t('overview.needsReview').toLowerCase()}`
      );
    }
    return parts.join(' · ');
  }

  // ------------------------------------------------------------- people

  const PEOPLE_LISTS: {
    key: PeopleListKey;
    label: string;
    help: string;
    metric: 'benefits' | 'expiry' | 'rate' | 'spent' | 'convert' | 'converted';
  }[] = [
    { key: 'topBenefitUsers', label: t('overview.topBenefitUsers'), help: t('overview.topBenefitUsersHelp'), metric: 'benefits' },
    { key: 'unusedBenefits', label: t('overview.unusedBenefits'), help: t('overview.unusedBenefitsHelp'), metric: 'expiry' },
    { key: 'mostConsistent', label: t('overview.mostConsistent'), help: t('overview.mostConsistentHelp'), metric: 'rate' },
    { key: 'paidButNoShow', label: t('overview.paidButNoShow'), help: t('overview.paidButNoShowHelp'), metric: 'spent' },
    { key: 'dropInsToConvert', label: t('overview.dropInsToConvert'), help: t('overview.dropInsToConvertHelp'), metric: 'convert' },
    // The other half of the drop-in list: proof the pitch works. Unlike every
    // other list here, the date range does not decide who is on it — a
    // conversion happened once, whenever it happened.
    { key: 'convertedCustomers', label: t('overview.convertedCustomers'), help: t('overview.convertedCustomersHelp'), metric: 'converted' },
  ];

  const current =
    PEOPLE_LISTS.find((l) => l.key === peopleList) ?? PEOPLE_LISTS[0];
  const peopleRows = members?.[current.key] ?? [];

  const metricTitle = {
    benefits: t('overview.benefitsUsed'),
    expiry: t('overview.expires'),
    rate: t('overview.checkInRate'),
    spent: t('overview.spent'),
    convert: t('overview.spentVsPlan'),
    converted: t('overview.spentBeforeJoining'),
  }[current.metric];

  /**
   * The metric cell, lifted out so the table and the card render the same
   * thing. Six lists share one column whose meaning changes per list; having
   * that logic exist twice is how the two views drift apart.
   */
  function PeopleMetric({ row }: { row: OverviewMember }) {
    if (current.metric === 'benefits') {
      return (
        <span className="font-semibold tabular-nums">
          {formatNumber(row.benefitsUsed)}
        </span>
      );
    }
    if (current.metric === 'expiry') {
      return (
        <Badge variant="warning">{formatDate(row.membershipEndDate)}</Badge>
      );
    }
    if (current.metric === 'rate') {
      return (
        <span className="font-semibold tabular-nums">
          {pct(row.checkInRate)}
        </span>
      );
    }
    if (current.metric === 'convert') {
      // Both halves, not just the difference: a plan price is monthly, so the
      // reader needs to see what is being compared before trusting the gap.
      return (
        <div>
          <p className="font-semibold tabular-nums">
            {formatCurrency(row.spent)}
          </p>
          {(members?.comparablePlanPrice ?? 0) > 0 && (
            <p
              className={cn(
                'text-xs',
                (row.overPlanPrice ?? 0) > 0
                  ? 'text-success'
                  : 'text-muted-foreground'
              )}
            >
              {t('overview.planPriceCompare', {
                plan:
                  language === 'en'
                    ? (members?.comparablePlanNameEn ?? '')
                    : (members?.comparablePlanNameEs ?? ''),
                price: formatCurrency(members?.comparablePlanPrice),
              })}
            </p>
          )}
        </div>
      );
    }
    if (current.metric === 'converted') {
      // What the membership replaced, plus when they came over. The spend alone
      // reads as a number with no story; the date is what makes it one.
      return (
        <div>
          <p className="font-semibold tabular-nums">
            {formatCurrency(row.spentBeforeMembership)}
          </p>
          <p className="text-muted-foreground text-xs">
            {t('overview.convertedOn', {
              date: formatDate(row.firstMembershipStartDate),
            })}
          </p>
          <p className="text-muted-foreground text-xs">
            {t('overview.benefitsSinceJoining', {
              count: row.benefitsUsedSinceMembership ?? 0,
            })}
          </p>
        </div>
      );
    }
    return (
      <span className="font-semibold tabular-nums">
        {formatCurrency(row.spent)}
      </span>
    );
  }

  /**
   * The plan cell. On the conversion list "no plan" is not the answer —
   * whether they ever had one is. A lapsed customer still turning up is a
   * win-back call, not a cold one.
   */
  function PeoplePlan({ row }: { row: OverviewMember }) {
    if (row.planNameEn || row.planNameEs) {
      return (
        <Badge>
          {localizedName(
            { nameEn: row.planNameEn, nameEs: row.planNameEs },
            language
          )}
        </Badge>
      );
    }
    if (row.lastMembershipEndDate) {
      return (
        <Badge variant="warning">
          {t('overview.lapsed')} · {formatDate(row.lastMembershipEndDate)}
        </Badge>
      );
    }
    return (
      <span className="text-muted-foreground text-xs">
        {t('overview.neverAMember')}
      </span>
    );
  }

  /**
   * A call list nobody can hand to whoever makes the calls is half a feature,
   * so the selected list exports as it appears — contact details first, then
   * the reason it is on this list.
   */
  function exportPeople() {
    if (!peopleRows.length) {
      toast.info(t('overview.noData'));
      return;
    }
    const header = [
      t('overview.name'),
      t('overview.email'),
      t('overview.phone'),
      t('overview.plan'),
      t('overview.lapsed'),
      t('overview.bookings'),
      t('overview.checkedIn'),
      t('overview.benefitsUsed'),
      t('overview.spent'),
      t('overview.converted'),
      t('overview.spentBeforeJoining'),
      t('overview.bookingsBeforeJoining'),
    ];
    const escape = (value: unknown) =>
      `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [
      header.map(escape).join(','),
      ...peopleRows.map((row) =>
        [
          row.name,
          row.email,
          formatPhone(row.phoneNumber),
          localizedName(
            { nameEn: row.planNameEn, nameEs: row.planNameEs },
            language
          ),
          row.lastMembershipEndDate ? formatDate(row.lastMembershipEndDate) : '',
          row.bookings,
          row.checkedIn,
          row.benefitsUsed,
          row.spent,
          row.firstMembershipStartDate
            ? formatDate(row.firstMembershipStartDate)
            : '',
          row.spentBeforeMembership,
          row.bookingsBeforeMembership,
        ]
          .map(escape)
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${current.key}-${start}-${end}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * One revenue source, with its plans or events behind an expander.
   *
   * Collapsed by default now that this table is full width and can be long: the
   * three source totals are the answer most of the time, and the breakdown is a
   * follow-up question. A source that took no money has nothing to open, so it
   * gets no expander rather than one that yields an empty list.
   *
   * The expanded list is paged rather than scrolled. A capped scroll region
   * announced nothing on desktop — the cut-off row was the only hint there was
   * more — and uncapped on a phone it buried whatever came after it. A pager
   * states the length up front and gets to the end in one tap either way.
   *
   * Paged here rather than on the API: the breakdown arrives whole with the
   * dashboard, so asking for a page would be a request that buys nothing.
   */
  function SourceGroup({ row }: { row: OverviewSource }) {
    const [open, setOpen] = useState(false);
    const [page, setPage] = useState(0);
    const breakdown = row.breakdown ?? [];
    const canOpen = breakdown.length > 0;
    const pageCount = Math.ceil(breakdown.length / BREAKDOWN_PAGE_SIZE);
    const visible = breakdown.slice(
      page * BREAKDOWN_PAGE_SIZE,
      (page + 1) * BREAKDOWN_PAGE_SIZE
    );

    const header = (
      <div className="flex w-full flex-col gap-2 p-3 text-left sm:flex-row sm:items-center sm:gap-6">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {canOpen ? (
            <ChevronRight
              className={cn(
                'text-muted-foreground size-4 shrink-0 transition-transform',
                open && 'rotate-90'
              )}
            />
          ) : (
            <span className="size-4 shrink-0" />
          )}
          <Badge
            variant="outline"
            style={{
              borderColor: SOURCE_VAR[row.source],
              color: SOURCE_VAR[row.source],
            }}
          >
            {t(sourceLabelKey(row.source))}
          </Badge>
          {canOpen && (
            <span className="text-muted-foreground text-xs">
              {t('overview.nItems', { count: breakdown.length })}
            </span>
          )}
        </div>

        <div className="sm:w-56">
          <Meter
            label={t('overview.share')}
            value={pct(row.share)}
            percent={pctNumber(row.share)}
            color={SOURCE_VAR[row.source]}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:w-80 sm:grid-cols-2">
          <Cell label={t('overview.collected')}>
            {formatCurrency(row.collected)}
          </Cell>
          <Cell label={t('overview.previousPeriod')}>
            {formatCurrency(row.previousCollected)}
            {row.change !== null && row.change !== undefined && (
              <span
                className={cn(
                  'ml-1 text-xs',
                  row.change >= 0 ? 'text-success' : 'text-destructive'
                )}
              >
                {row.change >= 0 ? '▲' : '▼'} {pct(Math.abs(row.change))}
              </span>
            )}
          </Cell>
        </div>

        <div className="text-right sm:w-32">
          <p className="font-semibold tabular-nums">
            {formatCurrency(row.net)}
          </p>
          <p className="text-muted-foreground text-xs">
            {t('overview.net').toLowerCase()}
          </p>
        </div>
      </div>
    );

    if (!canOpen) return header;

    return (
      <Collapsible
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          // Reopening a group lands at the top rather than wherever it was
          // paged to the last time.
          if (!next) setPage(0);
        }}
      >
        <CollapsibleTrigger className="hover:bg-accent/40 w-full transition-colors">
          {header}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="bg-muted/40 divide-y border-t">
            {visible.map((line) => (
              <div
                key={line.id}
                className="flex flex-col gap-1 px-3 py-2 pl-9 sm:flex-row sm:items-center sm:gap-6"
              >
                {/* Events for bookings, plans for memberships and gifts. */}
                <span className="min-w-0 flex-1 text-sm break-words">
                  {localizedName(line, language)}
                </span>
                {/* Share of this source, not of the whole business — said in
                    the label so nobody has to work out which denominator is in
                    play. */}
                <div className="sm:w-56">
                  <Meter
                    label={t('overview.shareOfSource')}
                    value={pct(line.share)}
                    percent={pctNumber(line.share)}
                    color={SOURCE_VAR[row.source]}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 sm:w-80">
                  <Cell label={t('overview.collected')}>
                    {formatCurrency(line.collected)}
                  </Cell>
                  <Cell label={t('overview.payments')}>
                    {formatNumber(line.payments)}
                  </Cell>
                </div>
                <div className="text-right sm:w-32">
                  <p className="text-sm font-semibold tabular-nums">
                    {formatCurrency(line.net)}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t('overview.net').toLowerCase()}
                  </p>
                </div>
              </div>
            ))}

            {pageCount > 1 && (
              <Pagination
                className="px-3 py-2 pl-9"
                page={page}
                size={BREAKDOWN_PAGE_SIZE}
                total={breakdown.length}
                onPageChange={setPage}
              />
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('overview.title')}
        description={t('overview.subtitle')}
      />

      <FilterBar
        busy={dashboard.isFetching}
        fields={filterFields}
        summary={summary}
      />

      {dashboard.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('overview.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void dashboard.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      <Tabs defaultValue="money" className="gap-5">
        <TabsList className="sm:w-auto">
          <TabsTrigger value="money" className="sm:flex-none sm:px-5">
            {t('overview.money')}
          </TabsTrigger>
          <TabsTrigger value="activity" className="sm:flex-none sm:px-5">
            {t('overview.activity')}
          </TabsTrigger>
          <TabsTrigger value="people" className="sm:flex-none sm:px-5">
            {t('overview.people')}
          </TabsTrigger>
        </TabsList>

        {/* ================================================== money ===== */}
        <TabsContent value="money" className="space-y-5">
          <Notice>{t('overview.moneyBasisNotice')}</Notice>

          {/* A plan does not have to belong to a location, so filtering to one
              silently drops that revenue. Saying how much is the difference
              between a caveat and a number nobody can reconcile. */}
          {(money?.excludedNoLocation ?? 0) > 0 && (
            <Notice tone="warning">
              {t('overview.excludedNoLocation', {
                amount: formatCurrency(money?.excludedNoLocation),
              })}
            </Notice>
          )}

          {/* Revenue first, then where it came from: you cannot read a split
              before you know what it is a split of. */}
          <StatGroup title={t('overview.revenue')}>
            <StatTile
              loading={loading}
              busy={busy}
              tone="primary"
              label={t('overview.netRevenue')}
              value={formatCurrency(money?.net)}
              sub={`${formatCurrency(money?.collected)} ${t('overview.collected').toLowerCase()}`}
              tooltip={t('overview.netRevenueTooltip')}
              onClick={() => drillInto(METRIC.collected, t('overview.collected'))}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="good"
              label={t('overview.paidOnline')}
              value={formatCurrency(money?.collectedOnline)}
              onClick={() =>
                drillInto(METRIC.paidOnline, t('overview.paidOnline'))
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="cash"
              label={t('overview.paidOffline')}
              value={formatCurrency(money?.collectedOffline)}
              onClick={() =>
                drillInto(METRIC.paidOffline, t('overview.paidOffline'))
              }
            />
            {/* Outstanding and Unbilled, added together — but they are not the
                same measurement, so the sub-line has to say so. Outstanding is
                unpaid balances on live bookings as at today, a position never
                filtered by the range; Unbilled is the list price of active
                memberships that were never billed at all. */}
            <StatTile
              loading={loading}
              busy={busy}
              tone="bad"
              label={t('overview.owedToYou')}
              value={formatCurrency(
                (money?.outstanding ?? 0) + (money?.unbilled ?? 0)
              )}
              sub={`${formatCurrency(money?.outstanding)} ${t('overview.bookings').toLowerCase()} · ${formatCurrency(money?.unbilled)} ${t('overview.memberships').toLowerCase()}`}
              tooltip={t('overview.owedToYouTooltip')}
              onClick={() => drillInto(METRIC.owedToYou, t('overview.owedToYou'))}
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('overview.averagePayment')}
              value={formatCurrency(money?.averagePayment)}
              sub={`${formatNumber(money?.payments)} ${t('overview.payments').toLowerCase()}`}
              onClick={() => drillInto(METRIC.collected, t('overview.payments'))}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="warn"
              label={t('overview.refunded')}
              value={formatCurrency(money?.refunded)}
              onClick={() => drillInto(METRIC.refunded, t('overview.refunded'))}
            />
            {/* Bookings an employee assigned with no payment taken. These have
                no purchase row at all, so they appear in no other figure here —
                not collected, not owed, not refunded. Comped and needs-review
                are both "no payment recorded" but they are not the same thing:
                one is a decision somebody signed, the other is money nobody can
                account for. */}
            <StatTile
              loading={loading}
              busy={busy}
              tone="warn"
              label={t('overview.noPaymentRecorded')}
              value={formatNumber(money?.noPaymentBookings)}
              sub={noPaymentLabel()}
              tooltip={t('overview.noPaymentRecordedTooltip')}
              onClick={() =>
                drillInto(METRIC.noPayment, t('overview.noPaymentRecorded'))
              }
            />
          </StatGroup>

          <StatGroup title={t('overview.revenueBySource')}>
            {bySource.map((row) => (
              <StatTile
                key={row.source}
                loading={loading}
                busy={busy}
                tone={
                  row.source === SOURCE.memberships
                    ? 'primary'
                    : row.source === SOURCE.bookings
                      ? 'good'
                      : 'cash'
                }
                label={t(sourceLabelKey(row.source))}
                value={formatCurrency(row.collected)}
                sub={changeLabel(row)}
                onClick={() =>
                  drillInto(
                    METRIC.collected,
                    t(sourceLabelKey(row.source)),
                    row.source
                  )
                }
              />
            ))}
          </StatGroup>

          {/* Its own band on purpose: none of these is income, and sitting them
              beside Net revenue is exactly how the legacy report ended up
              reporting money it never received. */}
          <StatGroup title={t('overview.notIncome')}>
            <StatTile
              loading={loading}
              busy={busy}
              label={t('overview.benefitValueGiven')}
              value={formatCurrency(money?.benefitValueGiven)}
              tooltip={t('overview.benefitValueGivenTooltip')}
              onClick={() =>
                drillInto(
                  METRIC.benefitValueGiven,
                  t('overview.benefitValueGiven')
                )
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('overview.voided')}
              value={formatCurrency(money?.voided)}
              tooltip={t('overview.voidedTooltip')}
              onClick={() => drillInto(METRIC.voided, t('overview.voided'))}
            />
          </StatGroup>

          {/* Gift vouchers belong to no location, so they are left out of a
              single-location view, the same as their revenue above. The sale is
              already in Net revenue; these add what it paid for and what is
              still owed as future bookings. */}
          {hasBookingVouchers && !model.locationId && (
            <VoucherReportTiles
              from={model.startDate}
              to={model.endDate}
              compact
            />
          )}

          <StatGroup title={t('overview.recurringVsOneOff')}>
            <StatTile
              loading={loading}
              busy={busy}
              tone="primary"
              label={t('overview.recurringRevenue')}
              value={formatCurrency(recurring?.recurringCollected)}
              sub={`${pct(recurring?.recurringShare)} ${t('overview.ofCollected')}`}
              tooltip={t('overview.recurringRevenueTooltip')}
              onClick={() =>
                drillInto(
                  METRIC.recurringRevenue,
                  t('overview.recurringRevenue')
                )
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('overview.oneOffRevenue')}
              value={formatCurrency(recurring?.oneOffCollected)}
              sub={`${formatNumber(recurring?.oneOffPayments)} ${t('overview.payments').toLowerCase()}`}
              onClick={() =>
                drillInto(METRIC.oneOffRevenue, t('overview.oneOffRevenue'))
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="good"
              label={t('overview.autoRenewing')}
              value={formatNumber(recurring?.activeAutoRenewing)}
              sub={t('overview.expectedToBillAgain')}
              tooltip={t('overview.autoRenewingTooltip')}
              onClick={() =>
                drillInto(METRIC.autoRenewing, t('overview.autoRenewing'))
              }
            />
          </StatGroup>

          {/* A plain stacked bar per day rather than a charting dependency:
              three series and a peak is all this needs, and it stays readable
              when a day is entirely one source. */}
          {(loading || series.length > 0) && (
            <section className="space-y-2">
              <SectionTitle title={t('overview.dailyRevenue')} />
              {loading ? (
                <Skeleton className="h-56 rounded-xl" />
              ) : (
                // `relative` outside the scroll box, so the busy spinner is
                // not scrolled sideways with the bars.
                <div className="relative rounded-xl">
                  <BusyOverlay show={busy} />
                  <div
                    className={cn(
                      'bg-card overflow-x-auto rounded-xl border p-3',
                      busy && 'opacity-60 transition-opacity'
                    )}
                  >
                    <div className="flex h-40 min-w-full items-end gap-1">
                      {series.map((day) => {
                        const scale = peak === 0 ? 0 : 140 / peak;
                        return (
                          <Tooltip key={day.date}>
                            <TooltipTrigger asChild>
                              {/* 10px minimum: the bar is also the tooltip's tap
                                  target, and a 6px target between two others is not
                                  one a thumb can hit. The wrapper scrolls, so the
                                  cost is a swipe, not a truncated chart. */}
                              <div className="flex min-w-2.5 flex-1 flex-col justify-end">
                                <div
                                  style={{
                                    height: (day.vouchers ?? 0) * scale,
                                    background: SOURCE_VAR[SOURCE.vouchers],
                                  }}
                                />
                                <div
                                  style={{
                                    height: (day.gifts ?? 0) * scale,
                                    background: SOURCE_VAR[SOURCE.gifts],
                                  }}
                                />
                                <div
                                  style={{
                                    height: (day.bookings ?? 0) * scale,
                                    background: SOURCE_VAR[SOURCE.bookings],
                                  }}
                                />
                                <div
                                  style={{
                                    height: (day.memberships ?? 0) * scale,
                                    background: SOURCE_VAR[SOURCE.memberships],
                                  }}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-semibold">{formatDate(day.date)}</p>
                              <p>
                                {t('overview.memberships')}:{' '}
                                {formatCurrency(day.memberships)}
                              </p>
                              <p>
                                {t('overview.bookings')}:{' '}
                                {formatCurrency(day.bookings)}
                              </p>
                              <p>
                                {t('overview.gifts')}: {formatCurrency(day.gifts)}
                              </p>
                              <p>
                                {t('overview.vouchers')}:{' '}
                                {formatCurrency(day.vouchers)}
                              </p>
                              <p className="font-semibold">
                                {t('overview.total')}: {formatCurrency(day.total)}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                    <div className="text-muted-foreground mt-2 flex flex-wrap gap-4 text-xs">
                      {[SOURCE.memberships, SOURCE.bookings, SOURCE.gifts, SOURCE.vouchers]
                        // A client without vouchers never has a voucher bar.
                        .filter((source) => hasBookingVouchers || source !== SOURCE.vouchers)
                        .map((source) => (
                          <span key={source} className="flex items-center gap-1.5">
                            <span
                              className="inline-block size-3 rounded-sm"
                              style={{ background: SOURCE_VAR[source] }}
                            />
                            {t(sourceLabelKey(source))}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Full width and stacked, in that order. "How it arrived" is a
              short fixed list and reads as the summary of the two; "Where it
              came from" opens into per-plan and per-event detail that needs the
              whole width to stay legible. */}
          <section className="space-y-2">
            <SectionTitle title={t('overview.howItArrived')} />
            {loading ? (
              <ListSkeleton />
            ) : byChannel.length === 0 ? (
              <EmptyList busy={busy} />
            ) : (
              <div className="relative divide-y rounded-xl border">
                <BusyOverlay show={busy} />
                {byChannel.map((row) => (
                  <div
                    key={row.method}
                    className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:gap-6"
                  >
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                      <span className="text-sm">
                        {t(paymentMethodLabelKey(row.method))}
                      </span>
                      {row.isOffline && (
                        <Badge variant="warning">{t('overview.offline')}</Badge>
                      )}
                    </div>
                    <div className="sm:w-56">
                      <Meter
                        label={t('overview.share')}
                        value={pct(row.share)}
                        percent={pctNumber(row.share)}
                      />
                    </div>
                    <div className="text-right sm:w-40">
                      <p className="font-semibold tabular-nums">
                        {formatCurrency(row.collected)}
                      </p>
                      <p className="text-muted-foreground text-xs tabular-nums">
                        {formatNumber(row.payments)}{' '}
                        {t('overview.payments').toLowerCase()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-2">
            <SectionTitle
              title={t('overview.whereItCameFrom')}
              help={t('overview.whereItCameFromHelp')}
            />
            {loading ? (
              <ListSkeleton />
            ) : bySource.length === 0 ? (
              <EmptyList busy={busy} />
            ) : (
              <div className="relative divide-y rounded-xl border">
                <BusyOverlay show={busy} />
                {bySource.map((row) => (
                  <SourceGroup key={row.source} row={row} />
                ))}
              </div>
            )}
          </section>
        </TabsContent>

        {/* =============================================== activity ===== */}
        <TabsContent value="activity" className="space-y-5">
          <Notice>{t('overview.activityBasisNotice')}</Notice>

          {/* Events are always tied to a location, so this view can only ever
              be memberships and gifts. An empty table with no explanation would
              read as missing data. */}
          {noLocation && (
            <Notice tone="warning">
              {t('overview.noLocationBookingsNotice')}
            </Notice>
          )}

          <StatGroup title={t('overview.activity')}>
            <StatTile
              loading={loading}
              busy={busy}
              tone="primary"
              label={t('overview.activeMemberships')}
              value={formatNumber(activity?.activeMemberships)}
              sub={`${formatNumber(activity?.newMemberships)} ${t('overview.newInPeriod')}`}
              onClick={() =>
                drillInto(
                  METRIC.activeMemberships,
                  t('overview.activeMemberships')
                )
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="warn"
              label={t('overview.expiringSoon')}
              value={formatNumber(activity?.expiringSoon)}
              tooltip={t('overview.expiringSoonTooltip')}
              onClick={() =>
                drillInto(METRIC.expiringSoon, t('overview.expiringSoon'))
              }
            />
            {/* Beside Expiring soon on purpose: the same retention question,
                one before the deadline and one just after it. */}
            <StatTile
              loading={loading}
              busy={busy}
              tone="bad"
              label={t('overview.recentlyExpired')}
              value={formatNumber(activity?.recentlyExpired)}
              sub={t('overview.recentlyExpiredSub')}
              tooltip={t('overview.recentlyExpiredTooltip')}
              onClick={() =>
                drillInto(METRIC.recentlyExpired, t('overview.recentlyExpired'))
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('overview.bookings')}
              value={formatNumber(activity?.bookings)}
              sub={`${formatNumber(activity?.eventsBooked)} ${t('overview.events').toLowerCase()}`}
              onClick={() => drillInto(METRIC.bookings, t('overview.bookings'))}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="good"
              label={t('overview.checkInRate')}
              value={pct(activity?.checkInRate)}
              sub={`${formatNumber(activity?.bookingsCheckedIn)} ${t('overview.checkedIn').toLowerCase()}`}
              tooltip={t('overview.checkInRateTooltip')}
              // The rate has no rows of its own; the list is the check-ins
              // counted in the sub-line, so it is titled as that.
              onClick={() => drillInto(METRIC.checkedIn, t('overview.checkedIn'))}
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('overview.usedABenefit')}
              value={formatNumber(activity?.benefitBookings)}
              onClick={() =>
                drillInto(METRIC.benefitBookings, t('overview.usedABenefit'))
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('overview.uniqueCustomers')}
              value={formatNumber(activity?.uniqueCustomers)}
              onClick={() =>
                drillInto(METRIC.uniqueCustomers, t('overview.uniqueCustomers'))
              }
            />
          </StatGroup>

          <section className="space-y-2">
            <SectionTitle title={t('overview.plansSelling')} />
            {loading ? (
              <ListSkeleton />
            ) : plans.length === 0 ? (
              <EmptyList busy={busy} />
            ) : (
              <div className="relative divide-y rounded-xl border">
                <BusyOverlay show={busy} />
                {plans.map((row) => (
                  <div key={row.subscriptionId} className="space-y-2.5 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <p className="min-w-0 text-sm font-medium">
                        {localizedName(row, language)}
                      </p>
                      <div className="shrink-0 text-right">
                        <p className="font-semibold tabular-nums">
                          {formatCurrency(row.revenue)}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {t('overview.revenue').toLowerCase()}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <Cell label={t('overview.activeNow')}>
                        {formatNumber(row.activeNow)}
                      </Cell>
                      <Cell label={t('overview.purchases')}>
                        {formatNumber(row.purchases)}
                      </Cell>
                      <Cell label={t('overview.new')}>
                        {formatNumber(row.new)}
                      </Cell>
                      <Cell label={t('overview.renewals')}>
                        {formatNumber(row.renewals)}
                      </Cell>
                    </div>
                    <Meter
                      label={t('overview.share')}
                      value={pct(row.share)}
                      percent={pctNumber(row.share)}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-2">
            <SectionTitle
              title={t('overview.topEvents')}
              help={t('overview.topEventsHelp')}
            />
            {loading ? (
              <ListSkeleton />
            ) : events.length === 0 ? (
              <EmptyList busy={busy} />
            ) : (
              <div className="relative divide-y rounded-xl border">
                <BusyOverlay show={busy} />
                {events.map((row) => (
                  <div key={row.eventId} className="space-y-2.5 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {row.name}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {row.locationName}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="font-semibold tabular-nums">
                          {formatCurrency(row.revenue)}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {t('overview.revenue').toLowerCase()}
                        </p>
                      </div>
                    </div>
                    {/* Bookings and used-a-benefit stay adjacent: a class packed
                        with members is popular and earns nothing directly, and
                        the pair is the whole story. */}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <Cell label={t('overview.bookings')}>
                        {formatNumber(row.bookings)}
                      </Cell>
                      <Cell label={t('overview.fillRate')}>
                        {pct(row.fillRate)}
                      </Cell>
                      <Cell label={t('overview.usedABenefit')}>
                        {formatNumber(row.usedBenefit)}
                      </Cell>
                      <Cell label={t('overview.checkedIn')}>
                        {pct(row.checkInRate)}
                      </Cell>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </TabsContent>

        {/* ================================================= people ===== */}
        <TabsContent value="people" className="space-y-4">
          <Notice>{t('overview.activityBasisNotice')}</Notice>

          {noLocation && (
            <Notice tone="warning">
              {t('overview.noLocationBookingsNotice')}
            </Notice>
          )}

          {/* Cards rather than a segmented control: these counts are worth
              seeing before you click. Whether anybody is on the "paid but never
              turned up" list is the question, and having to select it to find
              out was a click to learn nothing most days. */}
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
            {PEOPLE_LISTS.map((list) => {
              const selected = list.key === peopleList;
              const count = (members?.[list.key] ?? []).length;
              return (
                <button
                  key={list.key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setPeopleList(list.key)}
                  className={cn(
                    'bg-card relative overflow-hidden rounded-xl border p-3 pl-4 text-left transition-colors',
                    selected
                      ? 'border-primary bg-accent/50'
                      : 'hover:bg-accent/40'
                  )}
                >
                  {/* The count is the card's content: a refresh shows over it. */}
                  <BusyOverlay show={busy} size="sm" />
                  <span
                    aria-hidden
                    className={cn(
                      'absolute inset-y-0 left-0 w-[3px]',
                      selected ? 'bg-primary' : 'bg-border'
                    )}
                  />
                  <p
                    className={cn(
                      'text-xs leading-tight',
                      selected
                        ? 'text-primary font-semibold'
                        : 'text-muted-foreground'
                    )}
                  >
                    {list.label}
                  </p>
                  {loading ? (
                    <Skeleton className="mt-1.5 h-6 w-10" />
                  ) : (
                    <p className="mt-1 text-xl font-semibold tabular-nums">
                      {formatNumber(count)}
                    </p>
                  )}
                </button>
              );
            })}
          </div>

          <SectionTitle
            title={current.label}
            help={current.help}
            actions={
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={exportPeople}
              >
                <Download />
                {t('overview.exportList')}
              </Button>
            }
          />

          {loading ? (
            <ListSkeleton />
          ) : peopleRows.length === 0 ? (
            <EmptyList busy={busy} />
          ) : (
            <div className="relative divide-y rounded-xl border">
              <BusyOverlay show={busy} />
              {peopleRows.map((row) => (
                <div
                  key={row.userId}
                  className="space-y-2.5 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    {/* Name, email and phone as one contact block: these lists
                        exist to be acted on, and having to open each person to
                        find a number would defeat the point of them. */}
                    <div className="min-w-0 space-y-1">
                      <p className="truncate text-sm font-medium">{row.name}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        {row.email}
                      </p>
                      {row.phoneNumber && (
                        <p className="text-muted-foreground text-xs tabular-nums">
                          {formatPhone(row.phoneNumber)}
                        </p>
                      )}
                      <PeoplePlan row={row} />
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-muted-foreground text-xs">
                        {metricTitle}
                      </p>
                      <PeopleMetric row={row} />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <Cell label={t('overview.expires')}>
                      {row.membershipEndDate
                        ? formatDate(row.membershipEndDate)
                        : '—'}
                    </Cell>
                    <Cell label={t('overview.bookings')}>
                      {formatNumber(row.bookings)}
                    </Cell>
                    <Cell label={t('overview.checkedIn')}>
                      {formatNumber(row.checkedIn)}
                    </Cell>
                  </div>
                </div>
              ))}
            </div>
          )}

          {current.metric === 'rate' &&
            (members?.minBookingsForRates ?? 0) > 0 && (
              <p className="text-muted-foreground text-xs">
                {t('overview.minBookingsNote', {
                  count: members!.minBookingsForRates!,
                })}
              </p>
            )}
        </TabsContent>
      </Tabs>

      {/* Mounted once for every tile rather than per-tile: the metric is the
          identity of what is open, so there is no separate boolean to keep in
          step with it. */}
      <OverviewRowsSheet
        metric={drill?.metric ?? null}
        title={drill?.title ?? ''}
        source={drill?.source ?? null}
        model={model}
        onClose={() => setDrill(null)}
      />
    </div>
  );
}
